import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    getAddresses,
    addAddress as apiAddAddress,
    updateAddress as apiUpdateAddress,
    deleteAddress as apiDeleteAddress,
    setDefaultAddress as apiSetDefaultAddress
} from '../api';
import { useAuth } from '../AuthContext';

const AddressContext = createContext();

export const useAddress = () => {
    const context = useContext(AddressContext);
    if (!context) {
        throw new Error('useAddress must be used within AddressProvider');
    }
    return context;
};

export const AddressProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getActiveUserId = () => {
        if (currentUser?.id) return currentUser.id;
        if (currentUser?._id) return currentUser._id;
        try {
            const saved = localStorage.getItem('user');
            if (saved) {
                const parsed = JSON.parse(saved);
                return parsed.id || parsed._id;
            }
        } catch (e) { }
        return null;
    };

    const activeUserId = getActiveUserId();

    useEffect(() => {
        if (activeUserId) {
            loadAddresses(activeUserId);
        } else {
            setAddresses([]);
        }
    }, [activeUserId, currentUser]);

    const loadAddresses = async (uid) => {
        const targetId = uid || getActiveUserId();
        if (!targetId) return;
        try {
            setLoading(true);
            const data = await getAddresses(targetId);
            setAddresses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error loading addresses:', err);
            setError('Failed to load addresses');
        } finally {
            setLoading(false);
        }
    };

    const addAddress = async (addressData) => {
        const uid = getActiveUserId();
        if (!uid) return;
        try {
            const newAddress = await apiAddAddress(uid, addressData);
            setAddresses(prev => {
                const updated = [...prev, newAddress];
                return updated;
            });
            return newAddress;
        } catch (err) {
            console.error('Error adding address:', err);
            throw err;
        }
    };

    const updateAddress = async (addressId, addressData) => {
        const uid = getActiveUserId();
        if (!uid) return;
        try {
            await apiUpdateAddress(uid, addressId, addressData);
            setAddresses(prev =>
                prev.map(addr => (addr.id === addressId ? { ...addr, ...addressData } : addr))
            );
        } catch (err) {
            console.error('Error updating address:', err);
            throw err;
        }
    };

    const deleteAddress = async (addressId) => {
        const uid = getActiveUserId();
        if (!uid) return;
        try {
            await apiDeleteAddress(uid, addressId);
            setAddresses(prev => prev.filter(addr => addr.id !== addressId));
        } catch (err) {
            console.error('Error deleting address:', err);
            throw err;
        }
    };

    const setAsDefault = async (addressId) => {
        const uid = getActiveUserId();
        if (!uid) return;
        try {
            await apiSetDefaultAddress(uid, addressId);
            setAddresses(prev =>
                prev.map(addr => ({
                    ...addr,
                    isDefault: addr.id === addressId
                }))
            );
        } catch (err) {
            console.error('Error setting default address:', err);
            throw err;
        }
    };

    const getDefaultAddress = () => {
        return addresses.find(addr => addr.isDefault) || addresses[0];
    };

    return (
        <AddressContext.Provider
            value={{
                addresses,
                loading,
                error,
                addAddress,
                updateAddress,
                deleteAddress,
                setAsDefault,
                getDefaultAddress
            }}
        >
            {children}
        </AddressContext.Provider>
    );
};
